import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  CircularProgress,
  Alert,
  Fade,
} from '@mui/material';
import {
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as AssignmentIcon,
  CloudDone as UptimeIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useHeartbeat } from '../../contexts/HeartbeatContext';
import SuccessDialog from '../../components/SuccessDialog';
import api from '../../utils/api';

interface DashboardStats {
  totalActiveClients: number;
  totalAdminsOnline: number;
  totalLeadsProcessed: number;
  systemUptime: number;
  recentUploads: number;
}

const AdminDashboardContent: React.FC = () => {
  const theme = useTheme();
  const { connectionStatus } = useHeartbeat();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });

  const fetchDashboardStats = useCallback(async () => {
    try {
      setError(null);
      
      // Fetch all required data in parallel
      const [clientsResponse, adminsResponse, leadsResponse, uploadsResponse] = await Promise.all([
        api.get('/api/companies'),
        api.get('/api/auth/admins/status'),
        api.get('/api/upload/stats'),
        api.get('/api/upload/recent-stats')
      ]);

      // Calculate active clients
      const activeClients = clientsResponse.data.success ? 
        clientsResponse.data.data?.filter((company: any) => 
          company.users?.some((user: any) => user.isActive)
        ).length || 0 : 0;

      // Get online admins count
      const onlineAdmins = adminsResponse.data.stats?.online || 0;

      // Get total leads processed
      const totalLeads = leadsResponse.data.success ? leadsResponse.data.totalLeads || 0 : 0;

      // Get recent uploads (last 24 hours)
      const recentUploads = uploadsResponse.data.success ? uploadsResponse.data.last24Hours || 0 : 0;

      // Calculate system uptime (simplified - you can enhance this)
      const uptime = 99.9; // Placeholder - you can implement actual uptime calculation

      setStats({
        totalActiveClients: activeClients,
        totalAdminsOnline: onlineAdmins,
        totalLeadsProcessed: totalLeads,
        systemUptime: uptime,
        recentUploads: recentUploads
      });

    } catch (error: any) {
      console.error('❌ Dashboard stats fetch failed:', error);
      setError(error.response?.data?.message || 'Failed to fetch dashboard statistics');
      setSuccessDialogConfig({
        title: 'Error Loading Dashboard',
        message: 'Failed to load dashboard data',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDashboardStats();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchDashboardStats]);

  // Refresh when heartbeat updates
  useEffect(() => {
    if (connectionStatus === 'connected' && !loading) {
      fetchDashboardStats();
    }
  }, [connectionStatus, loading, fetchDashboardStats]);

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon, color, subtitle }) => (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 30px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`,
        },
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* First Row: Title and Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 500,
              mb: 1
            }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              color: theme.palette.text.primary,
            }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '23px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}20`,
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
        
        {/* Second Row: Subtitle */}
        {subtitle && (
          <Typography variant="caption" sx={{ 
            color: theme.palette.text.secondary,
            fontSize: '0.75rem'
          }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please try refreshing the page or contact support if the issue persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 700, 
          color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
          fontSize: '3rem', // Twice the size of h4 (1.5rem * 2)
          mb: 3
        }}>
          Admin Dashboard
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Clients"
            value={stats?.totalActiveClients || 0}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            color="#2196f3"
            subtitle="Active client companies"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Admins Online"
            value={stats?.totalAdminsOnline || 0}
            icon={<AdminIcon sx={{ fontSize: 28 }} />}
            color="#9c27b0"
            subtitle="Currently active administrators"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Leads Processed"
            value={stats?.totalLeadsProcessed || 0}
            icon={<AssignmentIcon sx={{ fontSize: 28 }} />}
            color="#4caf50"
            subtitle="Total leads across all companies"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="System Uptime"
            value={`${stats?.systemUptime || 0}%`}
            icon={<UptimeIcon sx={{ fontSize: 28 }} />}
            color="#ff9800"
            subtitle="Current server status"
          />
        </Grid>
      </Grid>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </Box>
  );
};

export default AdminDashboardContent;
